// Modal de confirmação pra cancelar assinatura — Story 3.6.
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { CancelSubscriptionDto } from '@control-center/shared';
import { formatDayBR } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useCancelSubscription } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionLite } from '../../lib/subscriptions-api';

interface CancelSubscriptionModalProps {
  subscription: SubscriptionLite | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CancelSubscriptionModal({
  subscription,
  open,
  onOpenChange,
}: CancelSubscriptionModalProps): JSX.Element | null {
  const [reason, setReason] = useState('');
  const [immediate, setImmediate] = useState(false);
  const cancel = useCancelSubscription();

  if (!subscription) return null;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!subscription) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      toast.error('Informe o motivo do cancelamento.');
      return;
    }
    try {
      const payload: CancelSubscriptionDto = { reason: trimmed, immediate };
      await cancel.mutateAsync({ id: subscription.id, payload });
      toast.success(
        immediate ? 'Assinatura cancelada.' : 'Cancelamento agendado pro fim do ciclo.',
      );
      setReason('');
      setImmediate(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos cancelar.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] rounded-[14px] border border-line bg-panel p-6 shadow-lg',
            'focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-[18px] font-semibold tracking-[-0.005em] text-ink">
                Cancelar assinatura
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                Cliente: <strong>{subscription.client.name}</strong> · Plano:{' '}
                {subscription.plan?.name ?? '—'}
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
            <Field label="Motivo" required>
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Por que está cancelando? (vai pro audit log)"
                  maxLength={500}
                  required
                />
              )}
            </Field>

            <label className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                className="mt-[2px] h-4 w-4 rounded border-line"
                checked={immediate}
                onChange={(e) => setImmediate(e.target.checked)}
              />
              <span>
                <strong>Cancelamento imediato</strong>
                <span className="block text-[11px] text-muted">
                  Por padrão (recomendado), cancela ao fim do ciclo (
                  {subscription.nextBillingDate
                    ? formatDayBR(subscription.nextBillingDate)
                    : 'data atual'}
                  ). Marque pra cancelar agora.
                </span>
              </span>
            </label>

            <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
              <PillButton
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={cancel.isPending}
              >
                Voltar
              </PillButton>
              <PillButton danger type="submit" disabled={cancel.isPending}>
                {cancel.isPending
                  ? 'Cancelando…'
                  : immediate
                    ? 'Cancelar agora'
                    : 'Agendar cancelamento'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
